import React from 'react';

interface State {
  error: Error | null;
}

/**
 * App-level error boundary. Catches crashes in any descendant component so a
 * single bad render doesn't blank the whole site. Shows a friendly recovery
 * card and logs to the console (Sentry would hook in here later).
 */
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] caught error:', error, info);
    // TODO: forward to Sentry when configured
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#f9fafb', fontFamily: 'Poppins,Arial,sans-serif' }}>
        <div style={{ maxWidth: 520, background: '#fff', borderRadius: 14, padding: 32, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 10 }}>Something went wrong</h1>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>
            We hit an unexpected error and couldn't finish loading this page. The SpeedyShip team has been notified. Try reloading the page — if it keeps happening, give us a call at <a href="tel:+18775890573" style={{ color: '#395ACB', fontWeight: 600, textDecoration: 'none' }}>1-877-589-0573</a>.
          </p>
          {import.meta.env.DEV && (
            <pre style={{ background: '#f3f4f6', padding: 12, borderRadius: 8, fontSize: 11, textAlign: 'left', overflow: 'auto', maxHeight: 180, color: '#374151', marginBottom: 16 }}>
              {String(this.state.error.stack || this.state.error.message)}
            </pre>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => { this.reset(); window.location.reload(); }} style={{ padding: '10px 22px', background: '#395ACB', color: '#fff', border: 'none', borderRadius: 50, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Reload Page
            </button>
            <a href="/" style={{ padding: '10px 22px', background: '#fff', color: '#395ACB', border: '1.5px solid #395ACB', borderRadius: 50, fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
              Go to Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
